import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  MedicalServices as MedicalServicesIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';
import AppointmentCalendar from '../../components/AppointmentCalendar/AppointmentCalendar';

// Doctor API functions
const DOCTOR_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/doctor';
const SLOTS_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/slots';

const fetchDoctorProfile = async (doctorId) => {
  if (!doctorId) {
    throw new Error('Doctor ID is required');
  }

  try {
    const url = `${DOCTOR_API_URL}?email=${encodeURIComponent(doctorId)}`;
    console.log('Fetching doctor profile from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error response:', errorData);
      
      if (response.status === 404) {
        // Doctor profile doesn't exist yet, return default structure
        return {
          doctorId: doctorId,
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          specialty: '',
          licenseNumber: '',
          hospital: '',
          address: '',
          bio: '',
          createdAt: new Date().toISOString()
        };
      }
      
      throw new Error(
        errorData?.message || `Failed to fetch doctor profile: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched doctor profile:', data);
    return data;
  } catch (error) {
    console.error('Error fetching doctor profile:', error.message);
    throw error;
  }
};

const createDoctorProfile = async (profileData) => {
  try {
    console.log('Creating doctor profile:', profileData);
    
    const response = await fetch(DOCTOR_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error creating profile:', errorData);
      throw new Error(
        errorData?.message || `Failed to create doctor profile: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully created doctor profile:', data);
    return data;
  } catch (error) {
    console.error('Error creating doctor profile:', error);
    throw error;
  }
};

const updateDoctorProfile = async (profileData) => {
  try {
    console.log('Updating doctor profile:', profileData);
    
    const response = await fetch(DOCTOR_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error updating profile:', errorData);
      throw new Error(
        errorData?.message || `Failed to update doctor profile: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully updated doctor profile:', data);
    return data;
  } catch (error) {
    console.error('Error updating doctor profile:', error);
    throw error;
  }
};

const fetchDoctorSlots = async (doctorId) => {
  if (!doctorId) {
    throw new Error('Doctor ID is required');
  }

  try {
    const url = `${SLOTS_API_URL}?doctorId=${encodeURIComponent(doctorId)}`;
    console.log('Fetching doctor slots from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch doctor slots: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched doctor slots:', data);
    
    // Transform slots into appointment-like objects for the calendar
    const slots = data.items || [];
    return slots.map(slot => ({
      requestId: `slot-${slot.assignedDoctorId}-${slot.slot}`,
      userId: slot.assignedDoctorId,
      patientName: 'Available Slot',
      status: 'AVAILABLE',
      scheduledAt: slot.slot, // Keep as UTC, the calendar will handle timezone conversion
      confirmedSlot: slot.slot,
      booked: slot.booked || false,
      reason: 'Doctor Availability'
    }));
  } catch (error) {
    console.error('Error fetching doctor slots:', error.message);
    throw error;
  }
};

const addDoctorSlots = async (doctorId, slotTimes) => {
  if (!doctorId || !slotTimes || !Array.isArray(slotTimes) || slotTimes.length === 0) {
    throw new Error('Doctor ID and slot times array are required');
  }

  try {
    const url = `${DOCTOR_API_URL}/slots`;
    const requestBody = {
      doctorId: doctorId,
      slots: slotTimes
    };
    
    console.log('Adding doctor slots to:', url);
    console.log('Request body:', JSON.stringify(requestBody, null, 2));
    console.log('DoctorId being sent:', doctorId);
    console.log('Slots being sent:', slotTimes);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error response from server:', errorData);
      console.error('Response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      throw new Error(
        errorData?.message || `Failed to add doctor slots: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully added doctor slots:', data);
    return data;
  } catch (error) {
    console.error('Error adding doctor slots:', error.message);
    throw error;
  }
};

const deleteDoctorSlots = async (doctorId, slotTimes) => {
  if (!doctorId || !slotTimes || !Array.isArray(slotTimes) || slotTimes.length === 0) {
    throw new Error('Doctor ID and slot times array are required');
  }

  try {
    const url = `${DOCTOR_API_URL}/slots`;
    console.log('Deleting doctor slots from:', url);
    console.log('Slots to delete:', slotTimes);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        doctorId: doctorId,
        slots: slotTimes
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to delete doctor slots: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully deleted doctor slots:', data);
    return data;
  } catch (error) {
    console.error('Error deleting doctor slots:', error.message);
    throw error;
  }
};

function Profile() {
  const theme = useTheme();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // Add slots dialog state
  const [addSlotsDialogOpen, setAddSlotsDialogOpen] = useState(false);
  const [deleteSlotsDialogOpen, setDeleteSlotsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [selectedDeleteTimes, setSelectedDeleteTimes] = useState([]);
  const [slotsToDelete, setSlotsToDelete] = useState([]);
  
  // New state for time range selection
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeRanges, setTimeRanges] = useState([]);
  
  // State for day action choice dialog
  const [dayActionDialogOpen, setDayActionDialogOpen] = useState(false);
  const [selectedDateForAction, setSelectedDateForAction] = useState(null);

  // Profile state
  const [profile, setProfile] = useState({
    doctorId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    licenseNumber: '',
    hospital: '',
    address: '',
    bio: '',
    createdAt: '',
    updatedAt: ''
  });

  // Keep track of original profile for cancel functionality
  const [originalProfile, setOriginalProfile] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  // Memoize currentDoctor to prevent infinite re-renders
  const currentDoctor = useMemo(() => {
    const doctor = {
      id: user?.username || 'unknown-doctor',
      name: user?.attributes?.name || user?.name || user?.username || 'Doctor',
      email: user?.attributes?.email || user?.email || '',
      phone: user?.attributes?.phone_number || user?.phone || ''
    };
    console.log('Current doctor info:', doctor);
    console.log('Doctor ID for API calls:', doctor.id);
    return doctor;
  }, [user?.username, user?.attributes?.name, user?.name, user?.attributes?.email, user?.email, user?.attributes?.phone_number, user?.phone]);

  // Fetch doctor profile with retry logic
  const fetchProfile = useCallback(async (isRetry = false) => {
    if (!user || !user.username) {
      setError('No valid doctor ID available');
      setLoading(false);
      return;
    }

    // Prevent infinite retries
    if (isRetry && retryCount >= MAX_RETRIES) {
      setError(`Failed to load profile after ${MAX_RETRIES} attempts. Please check your connection and try again.`);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('=== FETCHING DOCTOR PROFILE ===');
      console.log('Doctor ID:', user.username);
      console.log('Retry count:', retryCount);

      const doctorProfile = await fetchDoctorProfile(user.username);
      
      // Fill in data from authentication if profile is empty
      const enhancedProfile = {
        ...doctorProfile,
        doctorId: user.username,
        firstName: doctorProfile.firstName || currentDoctor.name.split(' ')[0] || '',
        lastName: doctorProfile.lastName || currentDoctor.name.split(' ').slice(1).join(' ') || '',
        email: doctorProfile.email || currentDoctor.email || '',
        phone: doctorProfile.phone || currentDoctor.phone || ''
      };

      setProfile(enhancedProfile);
      setOriginalProfile(enhancedProfile);
      setRetryCount(0); // Reset retry count on success
      console.log('Profile loaded successfully:', enhancedProfile);
    } catch (err) {
      console.error('Error fetching profile:', err);
      
      if (isRetry || retryCount >= MAX_RETRIES) {
        setError(`Failed to load profile: ${err.message}`);
      } else {
        // Automatic retry for the first failure
        console.log('Retrying profile fetch...');
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchProfile(true), 1000); // Retry after 1 second
        return; // Don't set loading to false yet
      }
    } finally {
      setLoading(false);
    }
  }, [user, currentDoctor, retryCount]);

  // Load profile on component mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Load slots when profile is loaded
  useEffect(() => {
    const loadSlots = async () => {
      if (!user?.username) {
        console.warn('No current doctor ID available for slots');
        return;
      }

      try {
        setSlotsLoading(true);
        console.log('Loading slots for doctor:', user.username);
        
        const slotsData = await fetchDoctorSlots(user.username);
        setSlots(slotsData);
        console.log('Slots loaded successfully:', slotsData);
      } catch (err) {
        console.error('Error fetching slots:', err);
        // Don't show error for slots, just log it and set empty array
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };

    // Load slots when we have a doctor ID and profile is not loading
    if (user?.username && !loading) {
      loadSlots();
    }
  }, [user?.username, loading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile({
      ...profile,
      [name]: value
    });
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel editing - restore original values
      setProfile(originalProfile);
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    if (!profile.doctorId) {
      setSnackbar({
        open: true,
        message: t.profile.invalidDoctorId,
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    
    try {
      console.log('Saving profile:', profile);
      
      let savedProfile;
      if (originalProfile.createdAt) {
        // Update existing profile
        savedProfile = await updateDoctorProfile(profile);
      } else {
        // Create new profile
        savedProfile = await createDoctorProfile(profile);
      }
      
      setProfile(savedProfile);
      setOriginalProfile(savedProfile);
      setEditMode(false);
      
      setSnackbar({
        open: true,
        message: t.profile.profileSavedSuccess,
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error saving profile:', err);
      setSnackbar({
        open: true,
        message: `${t.profile.profileSaveError}: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    setRetryCount(0); // Reset retry count
    fetchProfile();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Handle day click to show action options
  const handleDayClick = (date) => {
    // Only allow actions for current and future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setSnackbar({
        open: true,
        message: language === 'pt' 
          ? 'Só é possível modificar disponibilidade para hoje ou dias futuros' 
          : 'You can only modify availability for today or future dates',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedDateForAction(date);
    setDayActionDialogOpen(true);
  };

  // Handle opening add slots dialog for a specific date
  const handleAddSlotsForDate = (date) => {
    // Only allow adding availability for current and future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setSnackbar({
        open: true,
        message: language === 'pt' 
          ? 'Só é possível adicionar disponibilidade para hoje ou dias futuros' 
          : 'You can only add availability for today or future dates',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedDate(date);
    setSelectedTimes([]);
    setTimeRanges([]);
    setStartTime('');
    setEndTime('');
    setAddSlotsDialogOpen(true);
  };

  // Get existing slots for a specific date
  const getExistingSlotsForDate = (date) => {
    if (!date) return [];
    // Convert the date to local timezone for comparison
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return slots.filter(slot => {
      if (!slot.scheduledAt) return false;
      // Convert UTC slot time to local date for comparison
      const slotDate = new Date(slot.scheduledAt);
      const slotYear = slotDate.getFullYear();
      const slotMonth = String(slotDate.getMonth() + 1).padStart(2, '0');
      const slotDay = String(slotDate.getDate()).padStart(2, '0');
      const slotDateStr = `${slotYear}-${slotMonth}-${slotDay}`;
      
      return slotDateStr === dateStr;
    });
  };

  // Generate time slots for a day (9 AM to 6 PM, 20-minute intervals)
  const generateTimeSlots = (excludeExisting = false, forDate = null) => {
    const allSlots = [];
    for (let hour = 9; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 20) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        allSlots.push(timeString);
      }
    }
    
    if (!excludeExisting || !forDate) {
      return allSlots;
    }
    
    // Get existing slots for the date
    const existingSlots = getExistingSlotsForDate(forDate);
    const occupiedTimes = existingSlots.map(slot => {
      // Parse UTC time and convert to local time for display
      const slotDate = new Date(slot.scheduledAt);
      return `${slotDate.getHours().toString().padStart(2, '0')}:${slotDate.getMinutes().toString().padStart(2, '0')}`;
    });
    
    // Filter available slots
    let availableSlots = allSlots.filter(time => !occupiedTimes.includes(time));
    
    // For current day, also exclude past hours
    const today = new Date();
    const selectedDate = new Date(forDate);
    const isToday = selectedDate.toDateString() === today.toDateString();
    
    if (isToday) {
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      
      availableSlots = availableSlots.filter(time => {
        const [timeHour, timeMinute] = time.split(':').map(Number);
        const timeInMinutes = timeHour * 60 + timeMinute;
        const currentTimeInMinutes = currentHour * 60 + currentMinute;
        
        // Only show slots that are at least 20 minutes in the future
        return timeInMinutes > (currentTimeInMinutes + 20);
      });
    }
    
    return availableSlots;
  };

  // Handle time slot selection (legacy - for individual slots)
  const handleTimeSlotChange = (time) => {
    setSelectedTimes(prev => {
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      } else {
        return [...prev, time].sort();
      }
    });
  };

  // Handle adding a time range
  const handleAddTimeRange = () => {
    if (!startTime || !endTime) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Selecione horário de início e fim' : 'Please select start and end times',
        severity: 'warning'
      });
      return;
    }

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;
    
    if (endTimeInMinutes <= startTimeInMinutes) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Horário de fim deve ser depois do horário de início' : 'End time must be after start time',
        severity: 'warning'
      });
      return;
    }

    if ((endTimeInMinutes - startTimeInMinutes) < 20) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Duração mínima é de 20 minutos' : 'Minimum duration is 20 minutes',
        severity: 'warning'
      });
      return;
    }

    // Check for overlaps with existing ranges
    const hasOverlap = timeRanges.some(range => {
      const [rangeStartHour, rangeStartMinute] = range.startTime.split(':').map(Number);
      const [rangeEndHour, rangeEndMinute] = range.endTime.split(':').map(Number);
      const rangeStartInMinutes = rangeStartHour * 60 + rangeStartMinute;
      const rangeEndInMinutes = rangeEndHour * 60 + rangeEndMinute;
      
      return !(endTimeInMinutes <= rangeStartInMinutes || startTimeInMinutes >= rangeEndInMinutes);
    });

    if (hasOverlap) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Este horário sobrepõe-se com outro período já adicionado' : 'This time overlaps with another time range',
        severity: 'warning'
      });
      return;
    }

    // Check for conflicts with existing appointments/slots
    const existingSlots = getExistingSlotsForDate(selectedDate);
    const hasConflict = existingSlots.some(slot => {
      const slotDate = new Date(slot.scheduledAt);
      const slotHour = slotDate.getHours();
      const slotMinute = slotDate.getMinutes();
      const slotTimeInMinutes = slotHour * 60 + slotMinute;
      
      return slotTimeInMinutes >= startTimeInMinutes && slotTimeInMinutes < endTimeInMinutes;
    });

    if (hasConflict) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Este período conflita com agendamentos existentes' : 'This time range conflicts with existing appointments',
        severity: 'warning'
      });
      return;
    }

    const newRange = {
      id: Date.now(),
      startTime,
      endTime,
      duration: Math.floor((endTimeInMinutes - startTimeInMinutes) / 20) // in 20-minute slots
    };

    setTimeRanges(prev => [...prev, newRange]);
    setStartTime('');
    setEndTime('');
  };

  // Remove a time range
  const handleRemoveTimeRange = (rangeId) => {
    setTimeRanges(prev => prev.filter(range => range.id !== rangeId));
  };

  // Convert time ranges to individual 20-minute slots
  const convertRangesToSlots = () => {
    const allSlots = [];
    
    timeRanges.forEach(range => {
      const [startHour, startMinute] = range.startTime.split(':').map(Number);
      const [endHour, endMinute] = range.endTime.split(':').map(Number);
      
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      
      for (let timeInMinutes = startTimeInMinutes; timeInMinutes < endTimeInMinutes; timeInMinutes += 20) {
        const hours = Math.floor(timeInMinutes / 60);
        const minutes = timeInMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        allSlots.push(timeString);
      }
    });
    
    return allSlots;
  };

  // Handle adding slots
  const handleAddSlots = async () => {
    if (!user?.username || !selectedDate) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Dados inválidos' : 'Invalid data',
        severity: 'warning'
      });
      return;
    }

    // Convert time ranges to individual slots
    const allSlotsFromRanges = convertRangesToSlots();
    const allSelectedTimes = [...selectedTimes, ...allSlotsFromRanges];

    if (allSelectedTimes.length === 0) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Adicione pelo menos um período de disponibilidade' : 'Please add at least one availability period',
        severity: 'warning'
      });
      return;
    }

    try {
      setSlotsLoading(true);
      
      // Convert selected times to ISO format (local timezone)
      const isoSlots = allSelectedTimes.map(time => {
        const [hours, minutes] = time.split(':').map(Number);
        const slotDateTime = new Date(selectedDate);
        slotDateTime.setHours(hours, minutes, 0, 0);
        
        // Try different ISO formats to match what lambda expects
        const isoString = slotDateTime.toISOString();
        
        // Option 1: Remove milliseconds (.000)
        const withoutMilliseconds = isoString.replace('.000Z', 'Z');
        
        // Option 2: Remove Z timezone indicator  
        const withoutTimezone = isoString.replace('Z', '');
        
        // Option 3: Keep original format
        console.log('Original ISO:', isoString);
        console.log('Without milliseconds:', withoutMilliseconds);
        console.log('Without timezone:', withoutTimezone);
        
        // Let's try without milliseconds first
        return withoutMilliseconds;
      });

      await addDoctorSlots(user.username, isoSlots);
      
      // Refresh slots to show the new ones
      const updatedSlots = await fetchDoctorSlots(user.username);
      setSlots(updatedSlots);
      
      setAddSlotsDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'pt' 
          ? `${allSelectedTimes.length} horários adicionados com sucesso!`
          : `${allSelectedTimes.length} time slots added successfully!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error adding slots:', err);
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Erro ao adicionar horários' : 'Error adding time slots',
        severity: 'error'
      });
    } finally {
      setSlotsLoading(false);
    }
  };

  // Handle opening delete slots dialog for a specific date
  const handleDeleteSlotsForDate = (date) => {
    // Allow removing availability for current day and future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setSnackbar({
        open: true,
        message: language === 'pt' 
          ? 'Só é possível remover disponibilidade para hoje ou dias futuros' 
          : 'You can only remove availability for today or future dates',
        severity: 'warning'
      });
      return;
    }

    // Find all available slots for this date
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const availableSlotsForDate = slots.filter(slot => {
      if (!slot.scheduledAt || slot.status !== 'AVAILABLE') return false;
      // Convert UTC slot time to local date for comparison
      const slotDate = new Date(slot.scheduledAt);
      const slotYear = slotDate.getFullYear();
      const slotMonth = String(slotDate.getMonth() + 1).padStart(2, '0');
      const slotDay = String(slotDate.getDate()).padStart(2, '0');
      const slotDateStr = `${slotYear}-${slotMonth}-${slotDay}`;
      
      return slotDateStr === dateStr;
    });
    
    if (availableSlotsForDate.length === 0) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Nenhuma disponibilidade encontrada para esta data' : 'No availability found for this date',
        severity: 'info'
      });
      return;
    }
    
    setSelectedDate(date);
    setSlotsToDelete(availableSlotsForDate);
    setSelectedDeleteTimes([]);
    setDeleteSlotsDialogOpen(true);
  };

  // Handle delete time slot selection
  const handleDeleteTimeSlotChange = (slotTime) => {
    setSelectedDeleteTimes(prev => {
      if (prev.includes(slotTime)) {
        return prev.filter(t => t !== slotTime);
      } else {
        return [...prev, slotTime].sort();
      }
    });
  };

  // Handle deleting slots
  const handleDeleteSlots = async () => {
    if (!user?.username || selectedDeleteTimes.length === 0) {
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Selecione pelo menos um horário para remover' : 'Please select at least one time slot to remove',
        severity: 'warning'
      });
      return;
    }

    try {
      setSlotsLoading(true);
      
      await deleteDoctorSlots(user.username, selectedDeleteTimes);
      
      // Refresh slots to remove the deleted ones
      const updatedSlots = await fetchDoctorSlots(user.username);
      setSlots(updatedSlots);
      
      setDeleteSlotsDialogOpen(false);
      setSnackbar({
        open: true,
        message: language === 'pt' 
          ? `${selectedDeleteTimes.length} horários removidos com sucesso!`
          : `${selectedDeleteTimes.length} time slots removed successfully!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting slots:', err);
      setSnackbar({
        open: true,
        message: language === 'pt' ? 'Erro ao remover horários' : 'Error removing time slots',
        severity: 'error'
      });
    } finally {
      setSlotsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleRefresh}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            >
              {loading ? t.common.retrying : t.common.retry}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 0 }}>
            {t.profile.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t.profile.subtitle}
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{ 
            borderRadius: 8,
            px: 2,
            py: 1,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          {t.profile.refresh}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: theme.palette.primary.main,
                    mr: 3
                  }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Dr. {profile.firstName || 'First'} {profile.lastName || 'Last'}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                    {profile.specialty || t.profile.healthcareProvider}
                  </Typography>
                  <Chip 
                    label={t.profile.activeProvider} 
                    color="success" 
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
              <Box>
                {editMode ? (
                  <>
                    <IconButton 
                      color="primary" 
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ mr: 1 }}
                    >
                      {saving ? <CircularProgress size={24} /> : <SaveIcon />}
                    </IconButton>
                    <IconButton color="error" onClick={handleEditToggle} disabled={saving}>
                      <CancelIcon />
                    </IconButton>
                  </>
                ) : (
                  <IconButton color="primary" onClick={handleEditToggle}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.firstName}
                  name="firstName"
                  value={profile.firstName}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.lastName}
                  name="lastName"
                  value={profile.lastName}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.email}
                  name="email"
                  value={profile.email}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.phone}
                  name="phone"
                  value={profile.phone}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.medicalSpecialty}
                  name="specialty"
                  value={profile.specialty}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t.profile.licenseNumber}
                  name="licenseNumber"
                  value={profile.licenseNumber}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t.profile.hospitalClinic}
                  name="hospital"
                  value={profile.hospital}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t.profile.address}
                  name="address"
                  value={profile.address}
                  onChange={handleInputChange}
                  margin="normal"
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t.profile.professionalBio}
                  name="bio"
                  value={profile.bio}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={editMode ? 4 : 3}
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "standard"}
                  placeholder={t.profile.bioPlaceholder}
                />
              </Grid>
            </Grid>

            {!editMode && (
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
                sx={{ mt: 3, borderRadius: 8, px: 3, py: 1 }}
              >
                {t.profile.editProfile}
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Information Cards Column */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                    <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    {t.profile.contactInformation}
                  </Typography>
                  <List sx={{ py: 0 }}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <EmailIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <ListItemText
                        primary={t.profile.email}
                        secondary={profile.email || t.profile.notProvided}
                      />
                    </ListItem>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <PhoneIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <ListItemText
                        primary={t.profile.phone}
                        secondary={profile.phone || t.profile.notProvided}
                      />
                    </ListItem>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <LocationIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <ListItemText
                        primary={t.profile.address}
                        secondary={profile.address || t.profile.notProvided}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                    <MedicalServicesIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    {t.profile.professionalDetails}
                  </Typography>
                  <List sx={{ py: 0 }}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemText
                        primary={t.profile.specialty}
                        secondary={profile.specialty || t.profile.notSpecified}
                      />
                    </ListItem>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemText
                        primary={t.profile.licenseNumber}
                        secondary={profile.licenseNumber || t.profile.notProvided}
                      />
                    </ListItem>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <LocalHospitalIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
                      <ListItemText
                        primary={t.profile.hospitalClinic}
                        secondary={profile.hospital || t.profile.notProvided}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Account Information */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t.profile.accountInformation}
                  </Typography>
                  <List sx={{ py: 0 }}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemText
                        primary={t.profile.doctorId}
                        secondary={profile.doctorId || currentDoctor.id}
                      />
                    </ListItem>
                    {profile.createdAt && (
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemText
                          primary={t.profile.profileCreated}
                          secondary={new Date(profile.createdAt).toLocaleDateString()}
                        />
                      </ListItem>
                    )}
                    {profile.updatedAt && (
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemText
                          primary={t.profile.lastUpdated}
                          secondary={new Date(profile.updatedAt).toLocaleDateString()}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Availability Calendar - Full Width */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {t.profile?.availability || (language === 'pt' ? 'Disponibilidade' : 'Availability')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddSlotsForDate(new Date())}
                  disabled={slotsLoading}
                  size="small"
                >
                  {language === 'pt' ? 'Adicionar' : 'Add'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteSlotsForDate(new Date())}
                  disabled={slotsLoading}
                  size="small"
                >
                  {language === 'pt' ? 'Remover' : 'Remove'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={async () => {
                    if (!user?.username) return;
                    try {
                      setSlotsLoading(true);
                      const slotsData = await fetchDoctorSlots(user.username);
                      setSlots(slotsData);
                      setSnackbar({
                        open: true,
                        message: language === 'pt' ? 'Disponibilidade atualizada!' : 'Availability refreshed!',
                        severity: 'success'
                      });
                    } catch (err) {
                      console.error('Error refreshing slots:', err);
                      setSnackbar({
                        open: true,
                        message: language === 'pt' ? 'Erro ao atualizar disponibilidade' : 'Error refreshing availability',
                        severity: 'error'
                      });
                    } finally {
                      setSlotsLoading(false);
                    }
                  }}
                  disabled={slotsLoading}
                  size="small"
                >
                  {language === 'pt' ? 'Atualizar' : 'Refresh'}
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {language === 'pt' 
                ? 'Gerencie sua disponibilidade e visualize agendamentos existentes. Clique em um dia para definir horários disponíveis.'
                : 'Manage your availability and view existing appointments. Click on a day to set available time slots.'
              }
            </Typography>
            <AppointmentCalendar
              appointments={slots}
              language={language}
              onSelectEvent={(slot) => {
                console.log('Selected slot:', slot);
                // If it's an available slot, offer delete option
                if (slot.status === 'AVAILABLE') {
                  setSelectedDeleteTimes([slot.scheduledAt]);
                  setSlotsToDelete([slot]);
                  setSelectedDate(new Date(slot.scheduledAt));
                  setDeleteSlotsDialogOpen(true);
                }
              }}
              onStartVideoCall={(requestId) => {
                console.log('Start video call for:', requestId);
                // Handle video call start
              }}
              onViewDetails={(slot) => {
                console.log('View slot details:', slot);
                // Handle view slot details
              }}
              onDayClick={(date) => {
                // Handle day click to show availability options for that specific date
                handleDayClick(date);
              }}
            />
            {slotsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {language === 'pt' ? 'Carregando disponibilidade...' : 'Loading availability...'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Day Action Choice Dialog */}
      <Dialog 
        open={dayActionDialogOpen} 
        onClose={() => setDayActionDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6" component="div">
              {language === 'pt' ? 'Gerenciar Disponibilidade' : 'Manage Availability'}
            </Typography>
          </Box>
          {selectedDateForAction && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {language === 'pt' ? 'Data: ' : 'Date: '}
              {selectedDateForAction.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {language === 'pt' 
              ? 'Escolha uma ação para esta data:'
              : 'Choose an action for this date:'
            }
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setDayActionDialogOpen(false);
                handleAddSlotsForDate(selectedDateForAction);
              }}
              sx={{ 
                py: 1.5,
                justifyContent: 'flex-start',
                textAlign: 'left'
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {language === 'pt' ? 'Adicionar Disponibilidade' : 'Add Availability'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.875rem' }}>
                  {language === 'pt' ? 'Criar novos períodos de disponibilidade' : 'Create new availability periods'}
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                setDayActionDialogOpen(false);
                handleDeleteSlotsForDate(selectedDateForAction);
              }}
              sx={{ 
                py: 1.5,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderColor: alpha(theme.palette.error.main, 0.5),
                '&:hover': {
                  borderColor: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.04)
                }
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {language === 'pt' ? 'Remover Disponibilidade' : 'Remove Availability'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.875rem' }}>
                  {language === 'pt' ? 'Remover períodos de disponibilidade existentes' : 'Remove existing availability periods'}
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDayActionDialogOpen(false)}
          >
            {language === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Slots Dialog */}
      <Dialog 
        open={addSlotsDialogOpen} 
        onClose={() => setAddSlotsDialogOpen(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6" component="div">
              {language === 'pt' ? 'Adicionar Disponibilidade' : 'Add Availability'}
            </Typography>
          </Box>
          {selectedDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {language === 'pt' ? 'Data: ' : 'Date: '}
              {selectedDate.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {language === 'pt' 
              ? 'Defina períodos de disponibilidade para consultas (base de 20 minutos):'
              : 'Define availability periods for appointments (20-minute base):'
            }
          </Typography>
          
          {/* Show special message for current day */}
          {selectedDate && (() => {
            const today = new Date();
            const isToday = selectedDate.toDateString() === today.toDateString();
            
            if (isToday) {
              return (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="body2" sx={{ color: 'warning.dark', fontSize: '0.85rem' }}>
                    ⏰ {language === 'pt' 
                      ? 'Editando disponibilidade para hoje. Apenas horários futuros (20+ minutos) estão disponíveis.'
                      : 'Editing availability for today. Only future time slots (20+ minutes) are available.'
                    }
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          {/* Show existing slots for this date */}
          {selectedDate && (() => {
            const existingSlots = getExistingSlotsForDate(selectedDate);
            if (existingSlots.length > 0) {
              return (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'info.dark' }}>
                    {language === 'pt' ? 'Agendamentos Existentes:' : 'Existing Schedule:'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {existingSlots.map((slot) => {
                      // Convert UTC to local time for display
                      const slotDate = new Date(slot.scheduledAt);
                      const timeOnly = slotDate.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false
                      });
                      const statusLabel = slot.status === 'AVAILABLE' 
                        ? (language === 'pt' ? 'Disponível' : 'Available')
                        : (language === 'pt' ? 'Ocupado' : 'Busy');
                      return (
                        <Chip 
                          key={slot.requestId}
                          label={`${timeOnly} (${statusLabel})`}
                          size="small"
                          color={slot.status === 'AVAILABLE' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            }
            return null;
          })()}

          {/* Time Range Selection */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
              {language === 'pt' ? 'Adicionar Período de Disponibilidade:' : 'Add Availability Period:'}
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{language === 'pt' ? 'Hora de Início' : 'Start Time'}</InputLabel>
                  <Select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    label={language === 'pt' ? 'Hora de Início' : 'Start Time'}
                  >
                    {generateTimeSlots().map((time) => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{language === 'pt' ? 'Hora de Fim' : 'End Time'}</InputLabel>
                  <Select
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    label={language === 'pt' ? 'Hora de Fim' : 'End Time'}
                  >
                    {generateTimeSlots().map((time) => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Button
                  onClick={handleAddTimeRange}
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={!startTime || !endTime}
                  fullWidth
                >
                  {language === 'pt' ? 'Adicionar' : 'Add'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Display Added Time Ranges */}
          {timeRanges.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                {language === 'pt' ? 'Períodos Adicionados:' : 'Added Periods:'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {timeRanges.map((range) => (
                  <Box 
                    key={range.id}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1.5,
                      bgcolor: 'success.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {range.startTime} - {range.endTime}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {range.duration} × {language === 'pt' ? '20 min' : '20 min'} = {range.duration * 20} {language === 'pt' ? 'minutos' : 'minutes'}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => handleRemoveTimeRange(range.id)}
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Legacy individual time selection (optional) */}
          {(() => {
            const availableTimes = generateTimeSlots(true, selectedDate);
            if (availableTimes.length === 0) {
              return null;
            }
            
            return (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    {language === 'pt' ? 'Horários Individuais (Avançado)' : 'Individual Time Slots (Advanced)'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      {language === 'pt' ? 'Horários Livres:' : 'Available Time Slots:'}
                    </Typography>
                    <Grid container spacing={1}>
                      {availableTimes.map((time) => (
                        <Grid item xs={6} sm={4} md={3} key={time}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedTimes.includes(time)}
                                onChange={() => handleTimeSlotChange(time)}
                                size="small"
                              />
                            }
                            label={time}
                            sx={{ 
                              '& .MuiFormControlLabel-label': { 
                                fontSize: '0.875rem' 
                              }
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            );
          })()}
          
          {selectedTimes.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {language === 'pt' ? 'Horários Individuais Selecionados:' : 'Selected Individual Times:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedTimes.map((time) => (
                  <Chip 
                    key={time}
                    label={time}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setAddSlotsDialogOpen(false)}
            disabled={slotsLoading}
          >
            {language === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleAddSlots}
            variant="contained"
            disabled={slotsLoading || (timeRanges.length === 0 && selectedTimes.length === 0)}
            startIcon={slotsLoading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {slotsLoading 
              ? (language === 'pt' ? 'Adicionando...' : 'Adding...')
              : (language === 'pt' ? 'Adicionar Disponibilidade' : 'Add Availability')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Slots Dialog */}
      <Dialog 
        open={deleteSlotsDialogOpen} 
        onClose={() => setDeleteSlotsDialogOpen(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" component="div">
              {language === 'pt' ? 'Remover Disponibilidade' : 'Remove Availability'}
            </Typography>
          </Box>
          {selectedDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {language === 'pt' ? 'Data: ' : 'Date: '}
              {selectedDate.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {language === 'pt' 
              ? 'Selecione os horários que deseja remover da sua disponibilidade:'
              : 'Select the times you want to remove from your availability:'
            }
          </Typography>
          
          {slotsToDelete.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {language === 'pt' 
                ? 'Nenhuma disponibilidade encontrada para esta data.'
                : 'No availability found for this date.'
              }
            </Typography>
          ) : (
            <FormGroup>
              <Grid container spacing={1}>
                {slotsToDelete.map((slot) => {
                  // Convert UTC to local time for display
                  const slotDate = new Date(slot.scheduledAt);
                  const timeOnly = slotDate.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false
                  });
                  return (
                    <Grid item xs={6} sm={4} md={3} key={slot.requestId}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDeleteTimes.includes(slot.scheduledAt)}
                            onChange={() => handleDeleteTimeSlotChange(slot.scheduledAt)}
                            size="small"
                          />
                        }
                        label={timeOnly}
                        sx={{ 
                          '& .MuiFormControlLabel-label': { 
                            fontSize: '0.875rem' 
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </FormGroup>
          )}
          
          {selectedDeleteTimes.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'error.dark' }}>
                {language === 'pt' ? 'Horários a Remover:' : 'Times to Remove:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedDeleteTimes.map((slotTime) => {
                  // Convert UTC to local time for display
                  const slotDate = new Date(slotTime);
                  const timeOnly = slotDate.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false
                  });
                  return (
                    <Chip 
                      key={slotTime}
                      label={timeOnly}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDeleteSlotsDialogOpen(false)}
            disabled={slotsLoading}
          >
            {language === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleDeleteSlots}
            variant="contained"
            color="error"
            disabled={slotsLoading || selectedDeleteTimes.length === 0}
            startIcon={slotsLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {slotsLoading 
              ? (language === 'pt' ? 'Removendo...' : 'Removing...')
              : (language === 'pt' ? 'Remover Horários' : 'Remove Time Slots')
            }
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile; 